import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, Typography, Button, DatePicker, message, Spin } from "antd";
import {
  UserOutlined,
  TrophyOutlined,
  VideoCameraOutlined,
  BookOutlined,
  MessageOutlined,
  CopyOutlined,
  LinkOutlined,
  RiseOutlined,
  CloseOutlined,
  DollarOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import moment from "moment";

import { copyToClipboard } from "../../Utils/utilityFunctions";
import { getCoachDetails, getCoachAnalytics } from "../../Pages/api";
import "./CoachProfileModal.scss";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const CoachProfileModal = ({ visible, onClose, coachId }) => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [coachDetails, setCoachDetails] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  // Tier mapping
  const tierMapping = {
    tier1: "Basic",
    tier2: "Pro",
    tier3: "Advance",
    tier4: "Ultimate",
  };

  // Format earnings
  const formatEarnings = (amount) => {
    if (!amount) return "₹0";
    const amountInRupees = amount / 100; // Convert from paise to rupees
    // 1 crore
    if (amountInRupees >= 10000000) {
      return `₹${(amountInRupees / 10000000).toFixed(1)}Cr`;
    }
    // 1 lakh
    if (amountInRupees >= 100000) {
      return `₹${(amountInRupees / 100000).toFixed(1)}L`;
    }
    return `₹${amountInRupees.toFixed(2).toLocaleString("en-IN")}`;
  };

  // Format plan type (remove -plan suffix)
  const formatPlanType = (planType) => {
    if (!planType) return "N/A";
    return (
      planType.replace("-plan", "").charAt(0).toUpperCase() +
      planType.replace("-plan", "").slice(1)
    );
  };

  // Format date with null handling
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return moment(dateString).format("DD MMMM YYYY");
  };

  // Format phone number with dial code
  const formatPhone = (phone, dialCode) => {
    if (!phone) return "N/A";
    const phoneStr = phone.toString();
    return `${dialCode || "+91"}-${phoneStr}`;
  };

  // Copy to clipboard handler
  const handleCopy = (text, type) => {
    copyToClipboard(text, `${type} copied successfully`);
  };

  // Handle dashboard redirect
  const handleViewDashboard = () => {
    if (coachDetails?.host) {
      window.open(`https://${coachDetails.host}`, "_blank");
    }
  };

  // Reset modal data
  const resetModalData = () => {
    setCoachDetails(null);
    setAnalytics(null);
    setDateRange(null);
    setLoading(false);
    setInitialLoading(false);
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    // The useEffect will handle the API call when dateRange changes
  };

  // Define presets for the date picker
  const rangePresets = {
    Today: () => [moment().startOf("day"), moment().endOf("day")],
    "Last 7 Days": () => [
      moment().subtract(7, "days").startOf("day"),
      moment().endOf("day"),
    ],
    "Last 30 Days": () => [
      moment().subtract(30, "days").startOf("day"),
      moment().endOf("day"),
    ],
    "Last 90 Days": () => [
      moment().subtract(90, "days").startOf("day"),
      moment().endOf("day"),
    ],
  };

  // Load coach details
  const loadCoachDetails = async () => {
    if (!coachId) return;

    try {
      setLoading(true);
      const response = await getCoachDetails(coachId);
      if (response.result) {
        setCoachDetails(response.result);
      } else {
        message.error("No coach details found");
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to load coach details"
      );
      console.error("Error loading coach details:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load analytics
  const loadAnalytics = async () => {
    if (!coachId) return;

    try {
      setLoading(true);
      const query = {};
      if (dateRange && dateRange[0] && dateRange[1]) {
        query.startDate = dateRange[0].toISOString();
        query.endDate = dateRange[1].toISOString();
      }

      console.log("Loading analytics with query:", query); // Debug log

      const response = await getCoachAnalytics(
        coachId,
        query.startDate,
        query.endDate
      );
      if (response.result) {
        setAnalytics(response.result);
      } else {
        message.warning(
          "No analytics data available for the selected date range"
        );
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to load analytics"
      );
      console.error("Error loading analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load data when modal opens
  useEffect(() => {
    if (visible && coachId) {
      setInitialLoading(true);
      Promise.all([loadCoachDetails(), loadAnalytics()]).finally(() => {
        setInitialLoading(false);
      });
    }
  }, [visible, coachId]);

  // Load analytics when date range changes
  useEffect(() => {
    if (visible && coachId) {
      loadAnalytics();
    }
  }, [dateRange]);

  // Reset data when modal closes
  useEffect(() => {
    if (!visible) {
      resetModalData();
    }
  }, [visible]);

  if (!visible) return null;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width="70%"
      style={{
        top: "10%",
        height: "80vh",
        maxHeight: "80vh",
      }}
      closeIcon={<CloseOutlined />}
      className="coach-profile-modal"
      bodyStyle={{
        height: "80vh",
        maxHeight: "80vh",
        padding: 0,
        overflow: "hidden",
      }}
    >
      <div className="modal-scroll-container">
        <Spin spinning={loading || initialLoading}>
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header">
              <div className="header-left">
                <h1 className="modal-title">Coach Profile</h1>
              </div>
              <div className="header-right">
                <Button
                  className="dashboard-button"
                  icon={<LinkOutlined />}
                  onClick={handleViewDashboard}
                  disabled={!coachDetails?.host}
                >
                  View Dashboard
                </Button>
              </div>
            </div>

            {coachDetails ? (
              <>
                {/* Coach Information */}
                <div className="coach-info-section">
                  {/* First row: Name, ID, and Assignment */}
                  <div className="coach-header-row">
                    <div className="coach-name-id">
                      <h2 className="coach-name">{coachDetails.name}</h2>
                      <sup>
                        <span className="coach-id">ID: {coachDetails._id}</span>
                      </sup>
                    </div>
                    <div className="assignment-info">
                      Assigned to:{" "}
                      {coachDetails.accountManager?.name || "Unassigned"}
                    </div>
                  </div>

                  {/* Second row: Contact info and Status */}
                  <div className="contact-status-row">
                    <div className="contact-info">
                      <div className="contact-item">
                        <MailOutlined className="contact-icon" />
                        <span className="contact-text">
                          {coachDetails.email}
                        </span>
                        <CopyOutlined
                          className="copy-icon"
                          onClick={() =>
                            handleCopy(coachDetails.email, "Email")
                          }
                        />
                      </div>
                      <div className="contact-item">
                        <PhoneOutlined className="contact-icon" />
                        <span className="contact-text">
                          {formatPhone(
                            coachDetails.phone,
                            coachDetails.dialCode
                          )}
                        </span>
                        <CopyOutlined
                          className="copy-icon"
                          onClick={() =>
                            handleCopy(coachDetails.phone, "Phone")
                          }
                        />
                      </div>
                    </div>
                    <div
                      className={`status-badge ${
                        coachDetails.isActive ? "active" : "inactive"
                      }`}
                    >
                      {coachDetails.isActive ? "ACTIVE" : "INACTIVE"}
                    </div>
                  </div>
                </div>

                {/* Performance Metrics */}
                <div className="performance-metrics-section">
                  <div className="section-header">
                    <h3 className="section-title">Performance Metrics</h3>
                    <div className="date-picker-container">
                      <RangePicker
                        placeholder={["Start date", "End date"]}
                        onChange={(dates) => {
                          const providingDate = dates
                            ? [dates[0].startOf("day"), dates[1].endOf("day")]
                            : null;
                          handleDateRangeChange(providingDate);
                        }}
                        value={dateRange}
                        className="date-range-picker"
                        ranges={rangePresets}
                        showTime={false}
                        format="DD/MM/YYYY"
                        allowClear
                        placement="bottomRight"
                        disabledDate={(current) =>
                          current && current > moment().endOf("day")
                        }
                      />
                    </div>
                  </div>
                  {analytics ? (
                    <div className="metrics-grid">
                      <div className="metric-card">
                        <div className="metric-icon customers">
                          <UserOutlined />
                        </div>
                        <div className="metric-content">
                          <div className="metric-value">
                            {analytics.totalCustomers || 0} (
                            <span className="free-customers">
                              {analytics.totalFreeCustomers || 0} F
                            </span>
                            )
                          </div>
                          <div className="metric-title">Customers</div>
                        </div>
                      </div>
                      <div className="metric-card">
                        <div className="metric-icon services">
                          <TrophyOutlined />
                        </div>
                        <div className="metric-content">
                          <div className="metric-value">
                            {analytics.totalActiveMangos || 0}
                          </div>
                          <div className="metric-title">Services</div>
                        </div>
                      </div>
                      <div className="metric-card">
                        <div className="metric-icon workshops">
                          <VideoCameraOutlined />
                        </div>
                        <div className="metric-content">
                          <div className="metric-value">
                            {analytics.totalActiveWorkshops || 0}
                          </div>
                          <div className="metric-title">Workshops</div>
                        </div>
                      </div>
                      <div className="metric-card">
                        <div className="metric-icon courses">
                          <BookOutlined />
                        </div>
                        <div className="metric-content">
                          <div className="metric-value">
                            {analytics.totalActiveCourses || 0}
                          </div>
                          <div className="metric-title">Courses</div>
                        </div>
                      </div>
                      <div className="metric-card">
                        <div className="metric-icon one-on-ones">
                          <MessageOutlined />
                        </div>
                        <div className="metric-content">
                          <div className="metric-value">
                            {analytics.totalActiveOneToOne || 0}
                          </div>
                          <div className="metric-title">One-on-Ones</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="no-data-message">
                      No analytics data available
                    </div>
                  )}
                </div>

                {/* Details and Earnings */}
                <div className="details-section">
                  <div className="details-grid">
                    <div className="detail-card">
                      <div className="card-header">
                        <TrophyOutlined className="card-icon plan-details" />
                        <h4 className="card-title">Plan Details</h4>
                      </div>
                      <div className="card-content">
                        <div className="plan-tags">
                          <span
                            className={`plan-tag ${
                              coachDetails.whitelabelPlanType?.includes(
                                "freedom"
                              )
                                ? "freedom"
                                : "enterprise"
                            }`}
                          >
                            {formatPlanType(coachDetails.whitelabelPlanType)}
                          </span>
                          <span className="tier-tag">
                            {tierMapping[coachDetails.userTier] || "N/A"}
                          </span>
                          <span className="payment-tag">
                            {coachDetails.paymentPlan || "N/A"}
                          </span>
                        </div>
                        <div className="renewal-info">
                          <div className="info-item">
                            <span className="info-label">Last Renewal:</span>
                            <span className="info-value">
                              {formatDate(coachDetails.tierLastRenewedAt)}
                            </span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Expiry:</span>
                            <span className="info-value">
                              {formatDate(coachDetails.tierExpiry)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="detail-card">
                      <div className="card-header">
                        <TrophyOutlined className="card-icon commission-info" />
                        <h4 className="card-title">Commission Info</h4>
                      </div>
                      <div className="card-content">
                        <div className="commission-info">
                          <div className="info-item">
                            <span className="info-label">Commission:</span>
                            <span className="info-value">
                              {coachDetails.tmCommission || 0}%
                            </span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Convenience:</span>
                            <span className="info-value">
                              {coachDetails.convenienceFee || 0}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="detail-card">
                      <div className="card-header">
                        <RiseOutlined className="card-icon total-earnings" />
                        <h4 className="card-title">Total Earnings</h4>
                      </div>
                      <div className="card-content">
                        <div className="earnings-display">
                          <div className="earnings-amount">
                            {formatEarnings(analytics?.totalEarnings || 0)}
                          </div>
                          <div className="earnings-description">
                            Based on current date range
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="detail-card">
                      <div className="card-header">
                        <DollarOutlined className="card-icon commission-paid" />
                        <h4 className="card-title">Commission Paid</h4>
                      </div>
                      <div className="card-content">
                        <div className="earnings-display">
                          <div className="earnings-amount">
                            {formatEarnings(
                              analytics?.totalCommissionPaid || 0
                            )}
                          </div>
                          <div className="earnings-description">
                            Total commission paid to platform
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="loading-state">
                <Text type="secondary">Loading coach details...</Text>
              </div>
            )}
          </div>
        </Spin>
      </div>
    </Modal>
  );
};

CoachProfileModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  coachId: PropTypes.string,
};

export default CoachProfileModal;
